import type { FormInstance } from 'antd';

import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import type { ExtranetHotelPack } from 'app/typings/core/extranet/hotel/packs';

import {
  calculateStockNecessaryPerBooking,
  getPackScoreOnSingleDate,
} from '../../../utils/packs';

import { getExperiencePrices } from './ExperienceDiscount/_components/experiencePricing';

export const calculatePackPricing = (
  pack: ExtranetHotelPack,
  formValues: Record<string, number>,
  form: FormInstance<{
    averageRoomPrice: number;
    defaultNightRoomDiscount: number;
  }>,
  averageRoomPrice: number,
  defaultNightRoomDiscount: number,
  getExperience: (experienceId?: number) => ExtranetHotelExperience | undefined
) => {
  const mergedValues = pack.packExperiences.reduce((acc, pe) => {
    const key = `experienceDiscount-${pack.id}-${pe.experienceId}`;
    const fromForm = formValues[key] ?? form.getFieldValue(key);

    acc[key] = fromForm ?? Math.round((pe.discount ?? 0) * 100);

    return acc;
  }, {} as Record<string, number>);

  const { experiencesTotal, experiencesTotalOriginal } =
    pack.packExperiences.reduce(
      (acc, packExperience) => {
        if (!packExperience.isIncluded) {
          return acc;
        }

        const experienceDiscount =
          mergedValues[
            `experienceDiscount-${pack.id}-${packExperience.experienceId}`
          ];

        const experience = getExperience(packExperience.experienceId);
        const experiencePrices = getExperiencePrices(pack, experience);

        if (!experiencePrices) {
          return acc;
        }

        const quantity = calculateStockNecessaryPerBooking(packExperience);
        const adultPrice = experiencePrices.adultPrice;

        if (!adultPrice) {
          return acc;
        }

        return {
          experiencesTotalOriginal:
            acc.experiencesTotalOriginal + adultPrice * quantity,
          experiencesTotal:
            acc.experiencesTotal +
            adultPrice * quantity * (1 - experienceDiscount / 100),
        };
      },
      { experiencesTotal: 0, experiencesTotalOriginal: 0 }
    );

  const originalPackPrice = Math.round(
    (averageRoomPrice ?? 0) + experiencesTotalOriginal
  );
  const discountedPackPrice = Math.round(
    (averageRoomPrice ?? 0) * (1 - defaultNightRoomDiscount / 100) +
      experiencesTotal
  );

  const discount =
    originalPackPrice > 0
      ? Math.round(
          ((originalPackPrice - discountedPackPrice) / originalPackPrice) * 100
        )
      : 0;

  return {
    score: getPackScoreOnSingleDate(discount),
    originalPackPrice,
    discountedPackPrice,
    discount,
  };
};
